import React from "react";
import {
  Globe,
  SquareArrowOutUpRight,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  FileText,
  LayoutGrid,
  List,
} from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { formatVersion } from "../../utils/formatters";
import { StatusBadge } from "./StatusBadge";
import { RBACAction } from "./RBACButton";
import type { ModuleType } from "../../constants/rbac";
import { Tip } from "./ui/tooltip";
import { Dropdown } from "./Dropdown";

/* ────────────────────────────────────────────────────────────
 * EntityCardGrid — shared card-grid component used by Catalogs,
 * SSP, Profiles, and Mappings. Wraps:
 *   • optional top toolbar (page-owned filters on the left,
 *     "Rows per card" selector pinned to the right)
 *   • responsive card grid (1 / 2 / 3 / 4 cols)
 *   • pagination bar (bottom)
 *
 * Pages customise card content via render-function props.
 * Building blocks (StatTile, StatTiles, MetaDates, FlagChip,
 * VersionChip, TypeBadge, CardActions) are exported so pages
 * can compose them inside renderStats / renderFooterActions.
 * ─────────────────────────────────────────────────────────── */

/* ── Jurisdiction lookup (drives FlagChip) ── */
interface Jurisdiction {
  code: string;
  label: string;
}

const JURISDICTION_RULES: { match: RegExp; jur: Jurisdiction }[] = [
  { match: /\bim[\s-]?8\b|instruction manual\s*8|\bmas\b|trmg|singapore|\bsg\b/i, jur: { code: "SG", label: "Singapore" } },
  { match: /dora|gdpr|\beu\b|european|2022\/2554/i, jur: { code: "EU", label: "European Union" } },
  { match: /nist|fedramp|federal|800-53|\busa?\b/i, jur: { code: "US", label: "United States" } },
  { match: /ubuntu|canonical|united kingdom|britain|\bgb\b|\buk\b/i, jur: { code: "GB", label: "United Kingdom" } },
  { match: /japan|\bjp\b/i, jur: { code: "JP", label: "Japan" } },
];

export function getJurisdiction(name: string): Jurisdiction {
  const n = name || "";
  for (const r of JURISDICTION_RULES) if (r.match.test(n)) return r.jur;
  return { code: "INTL", label: "International" };
}

/* ── Status accent (border / glow / sheen by lifecycle status) ── */
export interface StatusAccent {
  bar: string;
  hoverBorder: string;
  hoverGlow: string;
  sheenVia: string;
}

export function statusAccent(status: string): StatusAccent {
  switch (status) {
    case "published":
      return {
        bar: "bg-emerald-500",
        hoverBorder: "hover:border-emerald-400 dark:hover:border-emerald-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(16,185,129,0.32)] dark:hover:shadow-[0_0_28px_rgba(16,185,129,0.36)]",
        sheenVia: "via-emerald-400/15 dark:via-emerald-400/20",
      };
    case "approved":
      return {
        bar: "bg-violet-500",
        hoverBorder: "hover:border-violet-400 dark:hover:border-violet-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(139,92,246,0.32)] dark:hover:shadow-[0_0_28px_rgba(139,92,246,0.36)]",
        sheenVia: "via-violet-400/15 dark:via-violet-400/20",
      };
    case "in_review":
      return {
        bar: "bg-blue-500",
        hoverBorder: "hover:border-blue-400 dark:hover:border-blue-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(59,130,246,0.32)] dark:hover:shadow-[0_0_28px_rgba(59,130,246,0.36)]",
        sheenVia: "via-blue-400/15 dark:via-blue-400/20",
      };
    case "rejected":
      return {
        bar: "bg-red-500",
        hoverBorder: "hover:border-red-400 dark:hover:border-red-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(239,68,68,0.32)] dark:hover:shadow-[0_0_28px_rgba(239,68,68,0.36)]",
        sheenVia: "via-red-400/15 dark:via-red-400/20",
      };
    case "superseded":
      return {
        bar: "bg-orange-500",
        hoverBorder: "hover:border-orange-400 dark:hover:border-orange-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(249,115,22,0.32)] dark:hover:shadow-[0_0_28px_rgba(249,115,22,0.36)]",
        sheenVia: "via-orange-400/15 dark:via-orange-400/20",
      };
    case "withdrawn":
    case "retired":
      return {
        bar: "bg-rose-600",
        hoverBorder: "hover:border-rose-400 dark:hover:border-rose-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(225,29,72,0.32)] dark:hover:shadow-[0_0_28px_rgba(225,29,72,0.36)]",
        sheenVia: "via-rose-400/15 dark:via-rose-400/20",
      };
    default: // draft
      return {
        bar: "bg-slate-300 dark:bg-white/25",
        hoverBorder: "hover:border-slate-400 dark:hover:border-white/35",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(100,116,139,0.30)] dark:hover:shadow-[0_0_28px_rgba(148,163,184,0.34)]",
        sheenVia: "via-slate-400/15 dark:via-slate-300/20",
      };
  }
}

/* ── FlagChip — auto-selects a country flag from a name string ── */
export function FlagChip({ name }: { name: string }) {
  const jur = getJurisdiction(name);
  const isIntl = jur.code === "INTL";
  return (
    <Tip label={jur.label}>
      <span className="inline-flex items-center justify-center">
        {isIntl ? (
          <Globe className="size-4 text-sky-600 dark:text-sky-400" />
        ) : (
          <ReactCountryFlag
            countryCode={jur.code}
            svg
            title={jur.label}
            className="ring-1 ring-[#cbd5e1] dark:ring-white/20 shadow-sm"
            style={{ width: "1.4rem", height: "1rem", borderRadius: "3px", objectFit: "cover", display: "block" }}
          />
        )}
      </span>
    </Tip>
  );
}

/* ── VersionChip — compact "V1" style chip ── */
export function VersionChip({
  version,
  tone = "light",
}: {
  version: number | string;
  tone?: "light" | "dark";
}) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] border text-[10px] font-bold tracking-widest whitespace-nowrap ${tone === "dark"
        ? "bg-white/5 text-white/70 border-white/10"
        : "bg-[#f8fafc] text-[#475569] border-[#e2e8f0] dark:bg-white/[0.04] dark:text-white/70 dark:border-white/[0.08]"
        }`}
    >
      {formatVersion(version)}
    </span>
  );
}

/* ── TypeBadge — pill chip (e.g. "Regulatory" / "Technology" / "SSP") ── */
export function TypeBadge({
  label,
  variant = "indigo",
}: {
  label: string;
  variant?: "sky" | "indigo";
}) {
  const c =
    variant === "sky"
      ? "bg-sky-50 text-sky-700 border-sky-200/70 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20"
      : "bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20";
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium border capitalize ${c}`}
    >
      {label}
    </span>
  );
}

/* ── TitleTooltip — wraps a heading in the small reusable tooltip ── */
export function TitleTooltip({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}) {
  if (!text) {
    return <span className="flex-1 min-w-0 block">{children}</span>;
  }
  return (
    <Tip
      label={text}
      className="flex-1 min-w-0 block"
      contentClassName="max-w-[280px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
    >
      <span className="block min-w-0">{children}</span>
    </Tip>
  );
}

/* ── StatTile — single icon + value + label cell (used inside StatTiles) ── */
export function StatTile({
  icon,
  accent,
  value,
  label,
  tooltip,
}: {
  icon: React.ReactNode;
  accent: "indigo" | "emerald";
  value: React.ReactNode;
  label: string;
  tooltip?: string;
}) {
  const tone =
    accent === "indigo"
      ? "text-indigo-500 bg-indigo-50 ring-indigo-100 dark:bg-indigo-500/10 dark:ring-indigo-500/20"
      : "text-emerald-500 bg-emerald-50 ring-emerald-100 dark:bg-emerald-500/10 dark:ring-emerald-500/20";
  const valueNode = (
    <span className="text-[15px] font-bold text-foreground tabular-nums leading-none truncate block">
      {value}
    </span>
  );
  return (
    <div className="flex flex-1 items-center gap-2 px-2.5 py-2 min-w-0">
      <span className={`flex size-6 flex-shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}>
        {icon}
      </span>
      <div className="flex flex-col gap-0.5 leading-none min-w-0 flex-1">
        {tooltip ? (
          <Tip
            label={tooltip}
            contentClassName="max-w-[200px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
          >
            {valueNode}
          </Tip>
        ) : (
          valueNode
        )}
        <span className="text-[10px] font-semibold capitalize tracking-normal text-[#94a3b8] dark:text-white/45 whitespace-nowrap truncate">
          {label}
        </span>
      </div>
    </div>
  );
}

/* ── StatTiles — container that lays out N StatTile children with dividers ── */
export function StatTiles({ children }: { children: React.ReactNode }) {
  const arr = React.Children.toArray(children);
  return (
    <div className="flex items-stretch rounded-xl border border-[#edf0f4] dark:border-white/[0.07] bg-[#f9fafb] dark:bg-white/[0.02]">
      {arr.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <div className="w-px self-stretch my-2.5 bg-[#e6eaef] dark:bg-white/[0.08]" />
          )}
          {c}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── MetaDates — Created above Updated, small text ── */
export function MetaDates({
  created,
  updated,
  createdLabel = "Created",
  updatedLabel = "Updated",
}: {
  created: string;
  updated: string;
  createdLabel?: string;
  updatedLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1 text-[10px] @[280px]:flex-row @[280px]:items-center @[280px]:gap-2.5 @[280px]:flex-wrap">
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap break-normal">
        <Calendar className="size-3 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
        <span className="font-semibold text-[#475569] dark:text-white/70">{createdLabel}</span>
        <span className="font-semibold text-[#0f172a] dark:text-white tabular-nums">
          {created}
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap break-normal">
        <Clock className="size-3 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
        <span className="font-semibold text-[#475569] dark:text-white/70">{updatedLabel}</span>
        <span className="font-semibold text-[#0f172a] dark:text-white tabular-nums">
          {updated}
        </span>
      </span>
    </div>
  );
}

/* ── CardActions — Export + Delete buttons (RBAC-gated delete) ── */
export interface CardActionsProps {
  onExport?: () => void;
  onDelete?: () => void;
  exporting?: boolean;
  deleting?: boolean;
  canDelete?: boolean;
  deleteModule?: ModuleType;
  exportTooltip?: string;
  deleteTooltip?: string;
  deleteDisabledTooltip?: string;
}

export function CardActions({
  onExport,
  onDelete,
  exporting,
  deleting,
  canDelete = true,
  deleteModule = "catalog",
  exportTooltip = "Export",
  deleteTooltip = "Delete",
  deleteDisabledTooltip = "This item cannot be deleted",
}: CardActionsProps) {
  return (
    <div className="flex items-center gap-1">
      {onExport && (
        <Tip label={exportTooltip}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExport();
            }}
            disabled={!!exporting}
            className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 text-[#64748b] dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-40"
          >
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <SquareArrowOutUpRight className="size-4" />}
          </button>
        </Tip>
      )}
      {onDelete && (
        <RBACAction module={deleteModule} action="delete">
          <Tip label={canDelete ? deleteTooltip : deleteDisabledTooltip}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={!!deleting || !canDelete}
              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-[#64748b] dark:text-white/60 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              {deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            </button>
          </Tip>
        </RBACAction>
      )}
    </div>
  );
}

/* ── EntityCard — internal: renders one card via the parent's render props ── */
interface EntityCardProps<T> {
  item: T;
  getStatus: (item: T) => string;
  renderTitle: (item: T) => React.ReactNode;
  renderHeaderRight?: (item: T) => React.ReactNode;
  renderBadges?: (item: T) => React.ReactNode;
  renderStats?: (item: T) => React.ReactNode;
  renderDates?: (item: T) => React.ReactNode;
  renderFooterActions?: (item: T) => React.ReactNode;
  onOpen: (item: T) => void;
}

function EntityCard<T>({
  item,
  getStatus,
  renderTitle,
  renderHeaderRight,
  renderBadges,
  renderStats,
  renderDates,
  renderFooterActions,
  onOpen,
}: EntityCardProps<T>) {
  const status = getStatus(item);
  const accent = statusAccent(status);
  return (
    <div
      onClick={() => onOpen(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
    >
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
      />
      <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex-1 min-w-0">{renderTitle(item)}</div>
          {renderHeaderRight && <div className="shrink-0">{renderHeaderRight(item)}</div>}
        </div>
        {renderBadges && (
          <div className="flex items-center gap-1.5 flex-wrap">{renderBadges(item)}</div>
        )}
        <div className="mt-auto flex flex-col gap-3">
          {renderStats && renderStats(item)}
          {renderDates && renderDates(item)}
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
            <StatusBadge
              status={status as Parameters<typeof StatusBadge>[0]["status"]}
              dot
            />
            <div className="flex-shrink-0">
              {renderFooterActions && renderFooterActions(item)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Pagination footer (windowed page numbers, mirrors DataTable styling) ── */
function pageWindow(current: number, total: number): (number | "dots")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "dots", total];
  if (current >= total - 3) return [1, "dots", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "dots", current - 1, current, current + 1, "dots", total];
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  itemLabel?: string;
}

export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  itemLabel = "items",
}: PaginationProps) {
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1;
  if (totalItems === 0) return null;
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015]">
      <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
        Showing{" "}
        <span className="font-semibold text-[#334155] dark:text-white/90">
          {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, totalItems)}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-[#334155] dark:text-white/90">{totalItems}</span>{" "}
        {itemLabel}
      </p>
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <button
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeft className="size-4" />
          </button>
          {pageWindow(page + 1, totalPages).map((p, idx) =>
            p === "dots" ? (
              <span
                key={`dots-${idx}`}
                className="flex size-8 items-center justify-center text-xs text-[#94a3b8] dark:text-white/40 select-none"
              >
                &hellip;
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p - 1)}
                className={`flex size-8 items-center justify-center text-xs font-semibold rounded-lg border transition-colors ${p - 1 === page
                  ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                  : "border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15"
                  }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Rows-per-card selector (rendered inside the top toolbar) ── */
export interface RowsPerCardSelectProps {
  value: number;
  onChange: (n: number) => void;
  options?: number[];
  label?: string;
}

export function RowsPerCardSelect({
  value,
  onChange,
  options = [10, 25, 50, 100],
  label = "Items per page",
}: RowsPerCardSelectProps) {
  // Always keep the current value selectable, even if it isn't one of the
  // provided options (e.g. a size carried over from the other view mode) — this
  // prevents the dropdown from rendering blank.
  const mergedOptions = options.includes(value)
    ? options
    : [...options, value].sort((a, b) => a - b);
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <label className="text-sm d whitespace-nowrap">{label}</label>
      <Dropdown
        value={String(value)}
        onChange={(v) => onChange(Number(v))}
        options={mergedOptions.map((n) => ({ value: String(n), label: String(n) }))}
        size="sm"
        align="end"
        className="!min-w-[80px]"
      />
    </div>
  );
}

/* ── EntityCardGrid — public component ── */
export interface EntityCardGridProps<T> {
  items: T[];
  loading?: boolean;
  /** Defaults to `(item) => item.id`. Override if items don't have an `id`. */
  getKey?: (item: T) => string;
  /** Lifecycle status string — drives the accent / sheen colour. */
  getStatus: (item: T) => string;
  /** Render the card title heading. */
  renderTitle: (item: T) => React.ReactNode;
  /** Render the top-right slot (e.g. country flag / globe). */
  renderHeaderRight?: (item: T) => React.ReactNode;
  /** Render badge chips below the title (type, version, etc.). */
  renderBadges?: (item: T) => React.ReactNode;
  /** Render the stat-tiles row (use <StatTiles><StatTile/><StatTile/></StatTiles>). */
  renderStats?: (item: T) => React.ReactNode;
  /** Render the dates row (defaults to nothing — pass <MetaDates ... /> to use). */
  renderDates?: (item: T) => React.ReactNode;
  /** Render the right side of the footer (use <CardActions ... />). */
  renderFooterActions?: (item: T) => React.ReactNode;
  /** Card click handler. */
  onOpen: (item: T) => void;

  /* Pagination + rows-per-card toolbar */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  /** Show the rows-per-card selector (top right). Omit to hide. */
  onPageSizeChange?: (s: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;

  /* Top toolbar — left slot for page-owned widgets (search, status filter) */
  toolbarLeft?: React.ReactNode;

  /* Empty state */
  emptyIcon?: React.ReactNode;
  emptyText?: string;

  /* Loading skeleton card height (px) */
  skeletonHeight?: number;

  /**
   * Optional table renderer. When provided, a Card/Table view-mode toggle is
   * shown in the top-right of the toolbar. In table mode, this function is
   * called with the current page's items and its output replaces the card grid.
   * Pages typically return a `<DataTable ... />` here.
   */
  renderTable?: (items: T[]) => React.ReactNode;
  /** Initial view mode when `renderTable` is provided. Defaults to `"card"`. */
  defaultViewMode?: "card" | "table";
}

/* ── View-mode toggle (Card / Table) ── */
function ViewModeToggle({
  value,
  onChange,
}: {
  value: "card" | "table";
  onChange: (v: "card" | "table") => void;
}) {
  const btn = (mode: "card" | "table", label: string, icon: React.ReactNode) => {
    const active = value === mode;
    return (
      <Tip label={label}>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={() => onChange(mode)}
          className={`flex size-8 items-center justify-center rounded-md transition-colors ${active
            ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
            : "text-[#64748b] dark:text-white/60 hover:text-foreground"
            }`}
        >
          {icon}
        </button>
      </Tip>
    );
  };
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
      {btn("card", "Card view", <LayoutGrid className="size-4" />)}
      {btn("table", "Table view", <List className="size-4" />)}
    </div>
  );
}

export function EntityCardGrid<T>(props: EntityCardGridProps<T>) {
  const {
    items,
    loading,
    getKey,
    getStatus,
    renderTitle,
    renderHeaderRight,
    renderBadges,
    renderStats,
    renderDates,
    renderFooterActions,
    onOpen,
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions,
    itemLabel = "items",
    toolbarLeft,
    emptyIcon,
    emptyText = "No items found",
    skeletonHeight = 260,
    renderTable,
    defaultViewMode = "card",
  } = props;

  const keyOf = getKey || ((it: T) => (it as { id?: string }).id ?? "");
  const [viewMode, setViewMode] = React.useState<"card" | "table">(
    defaultViewMode,
  );
  const canToggle = !!renderTable;
  const activeView: "card" | "table" = canToggle ? viewMode : "card";
  const hasToolbar = !!toolbarLeft || !!onPageSizeChange || canToggle;

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {hasToolbar && (
        <div className="flex-shrink-0 flex items-center gap-3 flex-wrap">
          {toolbarLeft}
          <div className="ml-auto flex items-center gap-3 flex-shrink-0">
            {canToggle && (
              <ViewModeToggle value={activeView} onChange={setViewMode} />
            )}
            {onPageSizeChange && (
              <RowsPerCardSelect
                value={pageSize}
                onChange={onPageSizeChange}
                // Dynamic default: card grids use multiples of 12 so the responsive
                // 1/2/3/4-column layout always fills complete rows (no gap); tables
                // keep the conventional 10/25/50/100. A page can still override.
                options={
                  pageSizeOptions ??
                  (activeView === "table"
                    ? [10, 25, 50, 100]
                    : [12, 24, 48, 96])
                }
                label={
                  activeView === "table"
                    ? "Rows per page"
                    : `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} per page`
                }
              />
            )}
          </div>
        </div>
      )}

      <div
        className={
          activeView === "table"
            ? "flex-1 min-h-0 flex flex-col"
            : "flex-1 min-h-0 overflow-y-auto pr-1 -mr-1"
        }
      >
        {loading ? (
          activeView === "table" ? (
            <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-card overflow-hidden">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse h-12 border-b border-[#eef2f6] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-2xl border-2 border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-3.5 flex flex-col gap-3"
                  style={{ minHeight: skeletonHeight }}
                >
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="h-3.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-3/4" />
                    <div className="size-4 rounded bg-[#e2e8f0] dark:bg-white/[0.09]" />
                  </div>
                  <div className="flex gap-1.5">
                    <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-16" />
                    <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-10" />
                  </div>
                  <div className="mt-auto flex flex-col gap-3">
                    <div className="h-14 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#edf0f4] dark:border-white/[0.07]" />
                    <div className="flex flex-col gap-1.5">
                      <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-2/3" />
                      <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-1/2" />
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                      <div className="h-4 rounded-full bg-[#e2e8f0] dark:bg-white/[0.09] w-20" />
                      <div className="flex gap-1">
                        <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                        <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-20 rounded-2xl border border-dashed border-[#e2e8f0] dark:border-white/[0.08]">
            {emptyIcon ?? <FileText className="size-8 text-[#cbd5e1] dark:text-white/30" />}
            <p className="text-sm text-[#94a3b8] dark:text-white/50">{emptyText}</p>
          </div>
        ) : activeView === "table" && renderTable ? (
          renderTable(items)
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
            {items.map((item) => (
              <EntityCard
                key={keyOf(item)}
                item={item}
                getStatus={getStatus}
                renderTitle={renderTitle}
                renderHeaderRight={renderHeaderRight}
                renderBadges={renderBadges}
                renderStats={renderStats}
                renderDates={renderDates}
                renderFooterActions={renderFooterActions}
                onOpen={onOpen}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && totalItems > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          itemLabel={itemLabel}
        />
      )}
    </div>
  );
}
